import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function CalendarPicker({ onDateRangeChange, initialRange }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedRange, setSelectedRange] = useState(initialRange || { start: null, end: null });
    const [selectingEnd, setSelectingEnd] = useState(false);

    // Set selectedRange from initialRange
    useEffect(() => {
        if (initialRange && initialRange.start && initialRange.end) {
            setSelectedRange(initialRange);
        }
    }, [initialRange]);

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];
        
        // Add empty cells for days before month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }
        
        // Add days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        
        return days;
    };

    const formatDate = (date) => {
        return date ? date.toISOString().split('T')[0] : '';
    };

    const isDateInRange = (date) => {
        if (!selectedRange.start || !selectedRange.end) return false;
        
        const dateStr = formatDate(date);
        return dateStr >= selectedRange.start && dateStr <= selectedRange.end;
    };

    const isDateSelected = (date) => {
        if (!date) return false;
        const dateStr = formatDate(date);
        return dateStr === selectedRange.start || dateStr === selectedRange.end;
    };

    const isDateStart = (date) => {
        if (!date || !selectedRange.start) return false;
        return formatDate(date) === selectedRange.start;
    };

    const isDateEnd = (date) => {
        if (!date || !selectedRange.end) return false;
        return formatDate(date) === selectedRange.end;
    };

    const handleDateClick = (date) => {
        if (!date) return;

        const clickedDateStr = formatDate(date);
        
        if (!selectingEnd) {
            // Select start date
            const newRange = { start: clickedDateStr, end: clickedDateStr };
            setSelectedRange(newRange);
            setSelectingEnd(true);
            onDateRangeChange(newRange);
        } else {
            // Select end date
            const startDate = new Date(selectedRange.start);
            const endDate = date;
            
            // Ensure start is before end
            const newRange = {
                start: formatDate(startDate <= endDate ? startDate : endDate),
                end: formatDate(startDate <= endDate ? endDate : startDate)
            };
            
            setSelectedRange(newRange);
            setSelectingEnd(false);
            onDateRangeChange(newRange);
        }
    };

    const navigateMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + direction);
        setCurrentDate(newDate);
    };

    const getMonthYearString = () => {
        return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    const clearSelection = () => {
        setSelectedRange({ start: null, end: null });
        setSelectingEnd(false);
        onDateRangeChange({ start: null, end: null });
    };

    const days = getDaysInMonth(currentDate);
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <div className="w-full">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={() => navigateMonth(-1)}
                    className="p-1 hover:bg-gray-100 rounded"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <h3 className="text-sm font-semibold text-gray-900">{getMonthYearString()}</h3>
                <button
                    onClick={() => navigateMonth(1)}
                    className="p-1 hover:bg-gray-100 rounded"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>

            {/* Week Days */}
            <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
                {days.map((date, index) => (
                    <div
                        key={index}
                        className={`aspect-square flex items-center justify-center text-sm rounded cursor-pointer transition-colors ${
                            !date ? '' :
                            isDateStart(date) ? 'bg-blue-500 text-white' :
                            isDateEnd(date) ? 'bg-blue-500 text-white' :
                            isDateInRange(date) ? 'bg-blue-100 text-blue-700' :
                            isDateSelected(date) ? 'bg-blue-200 text-blue-800' :
                            'hover:bg-gray-100'
                        }`}
                        onClick={() => date && handleDateClick(date)}
                    >
                        {date ? date.getDate() : ''}
                    </div>
                ))}
            </div>

            {/* Selected Range Display */}
            <div className="mt-4 pt-3 border-t border-gray-200">
                {/* <div className="text-xs text-gray-600 space-y-1">
                    <div>From: {selectedRange.start || 'Not selected'}</div>
                    <div>To: {selectedRange.end || 'Not selected'}</div>
                </div> */}
                
                {/* Clear Button */}
                <button
                    onClick={clearSelection}
                    className="mt-2 w-full px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                    Clear Selection
                </button>
            </div>

            {/* Instructions */}
            {!selectingEnd && (!selectedRange.start || !selectedRange.end) && (
                <div className="mt-2 text-xs text-gray-500 text-center">
                    Click a date to start selection
                </div>
            )}
            
            {selectingEnd && (
                <div className="mt-2 text-xs text-blue-600 text-center">
                    Click an end date to complete range
                </div>
            )}
        </div>
    );
}
